"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Download,
  Filter,
  Search,
  Eye,
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  LinkIcon,
} from "lucide-react"
import { Link } from "react-router-dom"
import bgImage from "@/assets/favicon.png"

interface DocumentStatus {
  id: string
  fileName: string
  uploadDate: string
  uploadTime: string
  fileSize: string
  status: "UPLOADED" | "OCR_PROCESSING" | "OCR_COMPLETED" | "LINKED_TO_GD" | "ERROR" | "ORPHANED"
  linkedGdNumber?: string
  uploadedBy: string
  processingTime?: string
  errorMessage?: string
  ocrAccuracy?: number
}

// Dummy data
const documentsData: DocumentStatus[] = [
  {
    id: "1",
    fileName: "theft_complaint_001.pdf",
    uploadDate: "15/01/2024",
    uploadTime: "14:30:00",
    fileSize: "2.4 MB",
    status: "LINKED_TO_GD",
    linkedGdNumber: "GD/2024/001",
    uploadedBy: "Inspector Sharma",
    processingTime: "45 seconds",
    ocrAccuracy: 95,
  },
  {
    id: "2",
    fileName: "assault_case_002.pdf",
    uploadDate: "16/01/2024",
    uploadTime: "09:15:00",
    fileSize: "1.8 MB",
    status: "LINKED_TO_GD",
    linkedGdNumber: "GD/2024/002",
    uploadedBy: "Sub-Inspector Patel",
    processingTime: "38 seconds",
    ocrAccuracy: 92,
  },
  {
    id: "3",
    fileName: "fraud_complaint_003.pdf",
    uploadDate: "17/01/2024",
    uploadTime: "11:45:00",
    fileSize: "3.1 MB",
    status: "OCR_COMPLETED",
    uploadedBy: "ASI Kumar",
    processingTime: "52 seconds",
    ocrAccuracy: 88,
  },
  {
    id: "4",
    fileName: "domestic_violence_004.pdf",
    uploadDate: "18/01/2024",
    uploadTime: "16:20:00",
    fileSize: "2.7 MB",
    status: "OCR_PROCESSING",
    uploadedBy: "Inspector Verma",
  },
  {
    id: "5",
    fileName: "scan_document_005.jpg",
    uploadDate: "19/01/2024",
    uploadTime: "10:30:00",
    fileSize: "4.2 MB",
    status: "ERROR",
    uploadedBy: "Sub-Inspector Singh",
    errorMessage: "Poor image quality - OCR failed",
  },
  {
    id: "6",
    fileName: "old_case_file_006.pdf",
    uploadDate: "20/01/2024",
    uploadTime: "13:15:00",
    fileSize: "1.5 MB",
    status: "ORPHANED",
    uploadedBy: "Constable Mehta",
    processingTime: "41 seconds",
    ocrAccuracy: 78,
  },
  {
    id: "7",
    fileName: "evidence_photo_007.png",
    uploadDate: "21/01/2024",
    uploadTime: "08:45:00",
    fileSize: "5.8 MB",
    status: "UPLOADED",
    uploadedBy: "Inspector Desai",
  },
]

export default function DocumentStatusReport() {
  const [filteredData, setFilteredData] = useState(documentsData)
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    status: "",
    uploadedBy: "",
  })
  const [searchTerm, setSearchTerm] = useState("")

  const statusOptions = ["All", "UPLOADED", "OCR_PROCESSING", "OCR_COMPLETED", "LINKED_TO_GD", "ERROR", "ORPHANED"]
  const uploaders = ["All", "Inspector Sharma", "Sub-Inspector Patel", "ASI Kumar", "Inspector Verma"]

  const applyFilters = () => {
    let filtered = documentsData

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.linkedGdNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.uploadedBy.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    if (filters.status && filters.status !== "All") {
      filtered = filtered.filter((item) => item.status === filters.status)
    }

    if (filters.uploadedBy && filters.uploadedBy !== "All") {
      filtered = filtered.filter((item) => item.uploadedBy === filters.uploadedBy)
    }

    setFilteredData(filtered)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      UPLOADED: { color: "bg-blue-100 text-blue-800", icon: Upload, text: "Uploaded" },
      OCR_PROCESSING: { color: "bg-yellow-100 text-yellow-800", icon: Clock, text: "Processing" },
      OCR_COMPLETED: { color: "bg-purple-100 text-purple-800", icon: FileText, text: "OCR Completed" },
      LINKED_TO_GD: { color: "bg-green-100 text-green-800", icon: CheckCircle, text: "Linked to GD" },
      ERROR: { color: "bg-red-100 text-red-800", icon: XCircle, text: "Error" },
      ORPHANED: { color: "bg-orange-100 text-orange-800", icon: AlertTriangle, text: "Orphaned" },
    }
    const config = statusConfig[status as keyof typeof statusConfig]
    const IconComponent = config.icon
    return (
      <Badge className={`${config.color} hover:${config.color}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    )
  }

  const exportToCSV = () => {
    const headers = [
      "File Name",
      "Upload Date",
      "Upload Time",
      "File Size",
      "Status",
      "Linked GD Number",
      "Uploaded By",
      "Processing Time",
      "OCR Accuracy",
      "Error Message",
    ]
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          row.fileName,
          row.uploadDate,
          row.uploadTime,
          row.fileSize,
          row.status,
          row.linkedGdNumber || "",
          row.uploadedBy,
          row.processingTime || "",
          row.ocrAccuracy || "",
          row.errorMessage || "",
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `document-status-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  const totalDocuments = filteredData.length
  const linkedDocuments = filteredData.filter((item) => item.status === "LINKED_TO_GD").length
  const orphanedDocuments = filteredData.filter((item) => item.status === "ORPHANED").length
  const errorDocuments = filteredData.filter((item) => item.status === "ERROR").length
  const processingDocuments = filteredData.filter((item) => item.status === "OCR_PROCESSING").length

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Document Upload Status Report</h1>
            <p className="text-gray-600 mt-1">Track uploaded files and their processing status</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{totalDocuments}</div>
                  <p className="text-sm text-gray-600">Total Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{linkedDocuments}</div>
                  <p className="text-sm text-gray-600">Linked to GD</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold text-orange-600">{orphanedDocuments}</div>
                  <p className="text-sm text-gray-600">Orphaned</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{errorDocuments}</div>
                  <p className="text-sm text-gray-600">Errors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{processingDocuments}</div>
                  <p className="text-sm text-gray-600">Processing</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Processing Overview */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black">Processing Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-black">Overall Processing Rate</span>
                <span className="text-sm text-gray-600">
                  {Math.round(((linkedDocuments + orphanedDocuments) / totalDocuments) * 100)}%
                </span>
              </div>
              <Progress value={((linkedDocuments + orphanedDocuments) / totalDocuments) * 100} className="w-full" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Linked ({linkedDocuments})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span>Orphaned ({orphanedDocuments})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span>Errors ({errorDocuments})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span>Processing ({processingDocuments})</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Uploaded By</Label>
                <Select
                  value={filters.uploadedBy}
                  onValueChange={(value) => setFilters({ ...filters, uploadedBy: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select uploader" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {uploaders.map((uploader) => (
                      <SelectItem key={uploader} value={uploader}>
                        {uploader}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by file name, GD number, uploader..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100">
            <TabsTrigger value="all" className="data-[state=active]:bg-white">
              All Documents
            </TabsTrigger>
            <TabsTrigger value="orphaned" className="data-[state=active]:bg-white">
              Orphaned ({orphanedDocuments})
            </TabsTrigger>
            <TabsTrigger value="errors" className="data-[state=active]:bg-white">
              Errors ({errorDocuments})
            </TabsTrigger>
            <TabsTrigger value="processing" className="data-[state=active]:bg-white">
              Processing ({processingDocuments})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">All Documents ({filteredData.length} records)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">File Name</TableHead>
                        <TableHead className="text-black font-semibold">Upload Date & Time</TableHead>
                        <TableHead className="text-black font-semibold">File Size</TableHead>
                        <TableHead className="text-black font-semibold">Status</TableHead>
                        <TableHead className="text-black font-semibold">Linked GD</TableHead>
                        <TableHead className="text-black font-semibold">Uploaded By</TableHead>
                        <TableHead className="text-black font-semibold">Processing Time</TableHead>
                        <TableHead className="text-black font-semibold">OCR Accuracy</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((item) => (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-black">{item.fileName}</TableCell>
                          <TableCell className="text-gray-600">
                            <div>
                              <div>{item.uploadDate}</div>
                              <div className="text-sm text-gray-500">{item.uploadTime}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">{item.fileSize}</TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell className="text-gray-600">
                            {item.linkedGdNumber ? (
                              <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800">
                                {item.linkedGdNumber}
                                <LinkIcon className="h-3 w-3 ml-1" />
                              </Button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-600">{item.uploadedBy}</TableCell>
                          <TableCell className="text-gray-600">{item.processingTime || "-"}</TableCell>
                          <TableCell className="text-gray-600">
                            {item.ocrAccuracy ? `${item.ocrAccuracy}%` : "-"}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orphaned">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  Orphaned Documents - Require Manual Linking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    These documents have been processed but are not linked to any GD record. They may need manual review
                    and linking.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">File Name</TableHead>
                        <TableHead className="text-black font-semibold">Upload Date</TableHead>
                        <TableHead className="text-black font-semibold">Uploaded By</TableHead>
                        <TableHead className="text-black font-semibold">OCR Accuracy</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .filter((item) => item.status === "ORPHANED")
                        .map((item) => (
                          <TableRow key={item.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">{item.fileName}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadDate}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadedBy}</TableCell>
                            <TableCell className="text-gray-600">
                              {item.ocrAccuracy ? `${item.ocrAccuracy}%` : "-"}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700">
                                  Link to GD
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="errors">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  Documents with Errors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    These documents encountered errors during processing and may need to be re-uploaded or manually
                    processed.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">File Name</TableHead>
                        <TableHead className="text-black font-semibold">Upload Date</TableHead>
                        <TableHead className="text-black font-semibold">Uploaded By</TableHead>
                        <TableHead className="text-black font-semibold">Error Message</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .filter((item) => item.status === "ERROR")
                        .map((item) => (
                          <TableRow key={item.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">{item.fileName}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadDate}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadedBy}</TableCell>
                            <TableCell className="text-red-600">{item.errorMessage}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button size="sm" className="bg-green-600 text-white hover:bg-green-700">
                                  Retry
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="processing">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  Currently Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    These documents are currently being processed. Processing typically takes 30-60 seconds depending on
                    file size and quality.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">File Name</TableHead>
                        <TableHead className="text-black font-semibold">Upload Time</TableHead>
                        <TableHead className="text-black font-semibold">File Size</TableHead>
                        <TableHead className="text-black font-semibold">Uploaded By</TableHead>
                        <TableHead className="text-black font-semibold">Progress</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .filter((item) => item.status === "OCR_PROCESSING")
                        .map((item) => (
                          <TableRow key={item.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">{item.fileName}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadTime}</TableCell>
                            <TableCell className="text-gray-600">{item.fileSize}</TableCell>
                            <TableCell className="text-gray-600">{item.uploadedBy}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={65} className="w-20" />
                                <span className="text-sm text-gray-600">65%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
